/**
 * Screenshot Utilities for Feedback System
 * Provides real screenshot capture with compression and thumbnail generation
 */

import html2canvas from 'html2canvas';
import imageCompression from 'browser-image-compression';
import { ScreenshotOptions } from '../types/feedback';

/**
 * Configuration for screenshot capture and compression
 */
const SCREENSHOT_CONFIG = {
  MAX_WIDTH: 1920,
  MAX_HEIGHT: 1080,
  THUMBNAIL_WIDTH: 150,
  MAX_FILE_SIZE_KB: 500,
  THUMBNAIL_MAX_SIZE_KB: 20,
  QUALITY: 0.8,
  THUMBNAIL_QUALITY: 0.6,
  // Mobile-specific configurations
  MOBILE_MAX_WIDTH: 800,
  MOBILE_MAX_HEIGHT: 600,
  MOBILE_QUALITY: 0.6,
  MOBILE_MAX_FILE_SIZE_KB: 200,
};

// Detect mobile device
const isMobileDevice = (): boolean => {
  return window.innerWidth < 768 || 
         /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Captures a screenshot of the current page or specific element
 */
export async function captureScreenshot(
  options: ScreenshotOptions = {
    quality: SCREENSHOT_CONFIG.QUALITY,
    format: 'webp',
    includeElement: false
  }
): Promise<{ fullImage: string; thumbnail: string; metadata: any }> {
  try {
    const isMobile = isMobileDevice();
    const element = options.targetElement || document.body;
    
    // Use mobile-optimized settings on mobile devices
    const maxWidth = isMobile ? SCREENSHOT_CONFIG.MOBILE_MAX_WIDTH : SCREENSHOT_CONFIG.MAX_WIDTH;
    const maxHeight = isMobile ? SCREENSHOT_CONFIG.MOBILE_MAX_HEIGHT : SCREENSHOT_CONFIG.MAX_HEIGHT;
    const quality = isMobile ? SCREENSHOT_CONFIG.MOBILE_QUALITY : (options.quality || SCREENSHOT_CONFIG.QUALITY);
    const scale = isMobile ? Math.min(window.devicePixelRatio || 1, 1.5) : (window.devicePixelRatio || 1);
    
    // Configure html2canvas options with mobile optimizations
    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: true,
      scale: scale,
      logging: false,
      width: Math.min(element.scrollWidth, maxWidth),
      height: Math.min(element.scrollHeight, maxHeight),
      windowWidth: Math.min(element.scrollWidth, maxWidth),
      windowHeight: Math.min(element.scrollHeight, maxHeight),
      backgroundColor: '#ffffff',
      // Mobile-specific optimizations
      foreignObjectRendering: !isMobile, // Disable on mobile for better performance
      imageTimeout: isMobile ? 5000 : 15000, // Shorter timeout on mobile
      removeContainer: true, // Clean up faster
    });

    // Convert canvas to blob for compression
    const blob = await canvasToBlob(canvas, options.format || 'webp', quality);
    
    // Compress the image with mobile-appropriate size limits
    const maxFileSize = isMobile ? SCREENSHOT_CONFIG.MOBILE_MAX_FILE_SIZE_KB : SCREENSHOT_CONFIG.MAX_FILE_SIZE_KB;
    const compressedImage = await compressImage(blob, maxFileSize);
    
    // Generate thumbnail with mobile-appropriate size
    const thumbnailWidth = isMobile ? 120 : SCREENSHOT_CONFIG.THUMBNAIL_WIDTH;
    const thumbnail = await generateThumbnail(canvas, thumbnailWidth);
    
    // Get metadata
    const metadata = {
      originalWidth: canvas.width,
      originalHeight: canvas.height,
      compressedSize: compressedImage.size,
      thumbnailSize: thumbnail.length * 0.75, // Approximate base64 size
      format: options.format || 'webp',
      timestamp: new Date().toISOString(),
      devicePixelRatio: window.devicePixelRatio || 1
    };

    return {
      fullImage: await blobToBase64(compressedImage),
      thumbnail,
      metadata
    };
  } catch (error) {
    console.error('Screenshot capture failed:', error);
    // Return a fallback placeholder
    return createPlaceholderScreenshot(error instanceof Error ? error.message : 'Screenshot failed');
  }
}

/**
 * Captures a screenshot of a specific element with highlighting
 */
export async function captureElementScreenshot(
  element: HTMLElement,
  options: ScreenshotOptions = { quality: 0.8, format: 'webp' }
): Promise<{ fullImage: string; thumbnail: string; elementData: any }> {
  try {
    // Get element position and dimensions
    const rect = element.getBoundingClientRect();
    const elementData = {
      selector: generateSelector(element),
      xpath: generateXPath(element),
      bounds: {
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY,
        width: rect.width,
        height: rect.height
      },
      tagName: element.tagName.toLowerCase(),
      className: element.className,
      id: element.id
    };

    // Add temporary highlight
    const originalStyle = element.style.cssText;
    element.style.outline = '3px solid #f59e0b';
    element.style.outlineOffset = '2px';
    
    // Capture the element
    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: true,
      scale: 2, // Higher quality for element captures
      logging: false,
      backgroundColor: '#ffffff'
    });
    
    // Remove highlight
    element.style.cssText = originalStyle;

    // Convert and compress
    const blob = await canvasToBlob(canvas, options.format || 'webp', options.quality);
    const compressedImage = await compressImage(blob, 200); // Smaller size for element screenshots
    const thumbnail = await generateThumbnail(canvas, 100); // Smaller thumbnail for elements

    return {
      fullImage: await blobToBase64(compressedImage),
      thumbnail,
      elementData
    };
  } catch (error) {
    console.error('Element screenshot capture failed:', error);
    return createPlaceholderElementScreenshot(element);
  }
}

/**
 * Compresses an image blob to target size
 */
async function compressImage(blob: Blob, maxSizeKB: number): Promise<Blob> {
  try {
    const options = {
      maxSizeMB: maxSizeKB / 1024,
      maxWidthOrHeight: SCREENSHOT_CONFIG.MAX_WIDTH,
      useWebWorker: true,
      fileType: blob.type || 'image/webp'
    };

    const compressedBlob = await imageCompression(blob, options);
    
    // If still too large, reduce quality further
    if (compressedBlob.size > maxSizeKB * 1024) {
      const furtherCompressed = await imageCompression(blob, {
        ...options,
        maxSizeMB: (maxSizeKB * 0.7) / 1024,
        initialQuality: 0.6
      });
      return furtherCompressed;
    }

    return compressedBlob;
  } catch (error) {
    console.warn('Image compression failed, returning original:', error);
    return blob;
  }
}

/**
 * Generates a thumbnail from canvas
 */
async function generateThumbnail(canvas: HTMLCanvasElement, maxWidth: number): Promise<string> {
  const thumbnailCanvas = document.createElement('canvas');
  const ctx = thumbnailCanvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not create thumbnail canvas context');
  }

  // Calculate thumbnail dimensions
  const scale = maxWidth / canvas.width;
  thumbnailCanvas.width = maxWidth;
  thumbnailCanvas.height = Math.round(canvas.height * scale);

  // Draw scaled image
  ctx.drawImage(canvas, 0, 0, thumbnailCanvas.width, thumbnailCanvas.height);

  // Convert to base64 with high compression
  return thumbnailCanvas.toDataURL('image/webp', SCREENSHOT_CONFIG.THUMBNAIL_QUALITY);
}

/**
 * Converts canvas to blob
 */
function canvasToBlob(canvas: HTMLCanvasElement, format: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      `image/${format}`,
      quality
    );
  });
}

/**
 * Converts blob to base64
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Generates a CSS selector for an element
 */
function generateSelector(element: HTMLElement): string {
  if (element.id) {
    return `#${element.id}`;
  }
  
  let selector = element.tagName.toLowerCase();
  
  if (element.className) {
    selector += `.${element.className.split(' ').join('.')}`;
  }
  
  // Add nth-child if needed
  const parent = element.parentElement;
  if (parent) {
    const siblings = Array.from(parent.children);
    const index = siblings.indexOf(element) + 1;
    if (siblings.length > 1) {
      selector += `:nth-child(${index})`;
    }
  }
  
  return selector;
}

/**
 * Generates XPath for an element
 */
function generateXPath(element: Element): string {
  if (element.id !== '') {
    return `//*[@id="${element.id}"]`;
  }
  
  if (element === document.body) {
    return '/html/body';
  }
  
  let ix = 0;
  const siblings = element.parentNode ? element.parentNode.childNodes : [];
  
  for (let i = 0; i < siblings.length; i++) {
    const sibling = siblings[i];
    
    if (sibling === element) {
      return generateXPath(element.parentNode as Element) + '/' + element.tagName.toLowerCase() + '[' + (ix + 1) + ']';
    }
    
    if (sibling.nodeType === 1 && (sibling as Element).tagName === element.tagName) {
      ix++;
    }
  }
  
  return '';
}

/**
 * Creates a placeholder screenshot when capture fails
 */
function createPlaceholderScreenshot(errorMessage: string): { fullImage: string; thumbnail: string; metadata: any } {
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 600;
  
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Create a simple error placeholder
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = '#dc2626';
    ctx.font = 'bold 24px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Screenshot Capture Failed', canvas.width / 2, canvas.height / 2 - 20);
    
    ctx.fillStyle = '#6b7280';
    ctx.font = '16px Inter, sans-serif';
    ctx.fillText(errorMessage, canvas.width / 2, canvas.height / 2 + 20);
  }
  
  const dataUrl = canvas.toDataURL('image/webp', 0.8);
  
  return {
    fullImage: dataUrl,
    thumbnail: dataUrl,
    metadata: {
      error: true,
      errorMessage,
      timestamp: new Date().toISOString()
    }
  };
}

/**
 * Creates a placeholder for element screenshots
 */
function createPlaceholderElementScreenshot(element: HTMLElement): { fullImage: string; thumbnail: string; elementData: any } {
  const rect = element.getBoundingClientRect();
  const canvas = document.createElement('canvas');
  canvas.width = Math.min(rect.width || 400, 800);
  canvas.height = Math.min(rect.height || 300, 600);
  
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);
    
    ctx.fillStyle = '#374151';
    ctx.font = '14px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Element Screenshot Placeholder', canvas.width / 2, canvas.height / 2);
  }
  
  const dataUrl = canvas.toDataURL('image/webp', 0.8);
  
  return {
    fullImage: dataUrl,
    thumbnail: dataUrl,
    elementData: {
      selector: generateSelector(element),
      xpath: generateXPath(element),
      bounds: {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      }
    }
  };
}

/**
 * Checks if screenshot capture is supported
 */
export function isScreenshotSupported(): boolean {
  return typeof html2canvas !== 'undefined' && 
         typeof HTMLCanvasElement !== 'undefined' &&
         typeof FileReader !== 'undefined';
}

/**
 * Gets the estimated size of a screenshot in KB
 */
export function estimateScreenshotSize(
  width: number, 
  height: number, 
  quality: number = 0.8
): number {
  // Rough estimation for WebP format
  return Math.round(width * height * 0.3 * quality / 1024);
}

/**
 * Requests permission for screen capture (for future use with Screen Capture API)
 */
export async function requestScreenCapturePermission(): Promise<boolean> {
  // HTML2Canvas doesn't require permissions
  // This is kept for future compatibility with Screen Capture API
  return true;
}